import { readInlineData, graphql } from 'react-relay';
import {
    eventNameConstants,
    getBidTrigger,
    interactionTypeConstants,
    stepInteractionConstants,
    trackEvent,
    TrackingObject,
    triggerConstants,
} from 'dibs-tracking';
import { BID_RESPONSE_ERRORS, BidErrorType } from '../../constants';

import type {
    bidModalTrackingHelpers_item$key,
    bidModalTrackingHelpers_item$data,
} from './__generated__/bidModalTrackingHelpers_item.graphql';
import { Maybe } from 'dibs-ts-utils/exports/Maybe';
import { getTrackedAuctionType } from '../../helpers/useTrackedAuctionType';

const { EVENT_AUCTION_BID } = eventNameConstants;
const {
    STEP_INTERACTION_ENTRY,
    STEP_INTERACTION_CONFIRM_BID_CLICK,
    STEP_INTERACTION_COMPLETE,
    STEP_INTERACTION_CONFIRM_MAX_BID_MODAL_CLICK,
} = stepInteractionConstants;
const { INTERACTION_TYPE_AUCTION } = interactionTypeConstants;
const { TRIGGER_INCREASE_MAX_BID_CLICK } = triggerConstants;

const itemFragment = graphql`
    fragment bidModalTrackingHelpers_item on Item @inline {
        serviceId
        relevantAuctionSolution(page: pdp) {
            minNextBidAmount {
                amount
            }
        }
        getCategoriesByLevel(level: 1) {
            name
        }
        vertical
    }
`;

type UseItemDataReturns = {
    minBidAmount: number;
    auctionType: string;
};
const mapItemDataToTrackingProps = (
    item: bidModalTrackingHelpers_item$data | null
): UseItemDataReturns => {
    const { relevantAuctionSolution, getCategoriesByLevel, vertical } = item || {};
    const minBidAmount = relevantAuctionSolution?.minNextBidAmount?.amount || 0;
    const L1Category = getCategoriesByLevel?.[0]?.name || '';
    const auctionType = getTrackedAuctionType(vertical, L1Category);

    return {
        minBidAmount,
        auctionType,
    };
};

export const TRACKING_LABEL_BID_CLICK = 'bid click - auction PDP';

export function trackAuctionEvent({
    isInteractiveEvent = false,
    interaction_type = INTERACTION_TYPE_AUCTION,
    ...rest
}: TrackingObject): void {
    trackEvent({
        ...rest,
        category: 'auction',
        interaction_type,
        isInteractiveEvent,
    });
}

export function trackAuctionBidClick({
    item: itemRef,
    hasBid,
}: {
    item: bidModalTrackingHelpers_item$key;
    hasBid: boolean;
}): void {
    const item = readInlineData(itemFragment, itemRef);
    const { minBidAmount, auctionType } = mapItemDataToTrackingProps(item);

    trackAuctionEvent({
        action: 'auction bid entry',
        label: 'auction PDP - bid click',
        itemId: item?.serviceId || '',
        value: String(minBidAmount),
        isInteractiveEvent: true,
        eventName: EVENT_AUCTION_BID,
        step_interaction_name: STEP_INTERACTION_ENTRY,
        trigger: getBidTrigger(hasBid),
        additional: {
            auction_type: auctionType,
        },
    });
}

export function trackAuctionBidConfirmClick({
    bidAmount,
    item: itemRef,
    hasBid,
    largeBid,
}: {
    bidAmount: number;
    item: bidModalTrackingHelpers_item$key | null;
    hasBid: boolean;
    largeBid?: boolean;
}): void {
    const item = readInlineData(itemFragment, itemRef);
    const { auctionType } = mapItemDataToTrackingProps(item);

    trackAuctionEvent({
        action: 'auction bid interaction',
        label: `auction PDP - confirm bid click${largeBid ? ' large bid' : ''}`,
        itemId: item?.serviceId || '',
        value: String(bidAmount),
        isInteractiveEvent: true,
        eventName: EVENT_AUCTION_BID,
        step_interaction_name: STEP_INTERACTION_CONFIRM_BID_CLICK,
        trigger: getBidTrigger(hasBid),
        additional: {
            auction_type: auctionType,
        },
    });
}

function getAuctionTimeLeftInMinutes(endDate: string): number {
    const diff = Math.round((Date.parse(endDate) - Date.now()) / 1000 / 60);
    return Math.max(diff, 0);
}

export function trackAuctionBidTimeLeft({ endDate }: { endDate?: string | null }): void {
    const minutesLeft = getAuctionTimeLeftInMinutes(endDate || '');
    trackAuctionEvent({
        action: 'place bid - auction time left',
        label: String(minutesLeft),
    });
}

export function trackAuctionBidComplete({
    bidAmount,
    bidErrorType,
    item: itemRef,
    hasBid,
    endDate,
}: {
    bidAmount: number;
    bidErrorType: BidErrorType | null;
    item: bidModalTrackingHelpers_item$key | null;
    hasBid: boolean;
    endDate: Maybe<string>;
}): void {
    const item = readInlineData(itemFragment, itemRef);
    const { auctionType } = mapItemDataToTrackingProps(item);
    const minutesLeft = getAuctionTimeLeftInMinutes(endDate || '');

    let bidStatus = '';
    switch (true) {
        case bidErrorType === null:
            bidStatus = 'successful';
            break;
        case bidErrorType === BID_RESPONSE_ERRORS.CLOSE_TO_RESERVE:
            bidStatus = 'successful - reserve not met but close';
            break;
        case bidErrorType === BID_RESPONSE_ERRORS.RESERVE_NOT_MET:
            bidStatus = 'successful - reserve not met';
            break;
        case bidErrorType === BID_RESPONSE_ERRORS.OUTBID:
            bidStatus = 'successful - outbid';
        case bidErrorType === BID_RESPONSE_ERRORS.OUTBID_RESERVE_NOT_MET:
            bidStatus = 'successful - auto outbid';
            break;
    }

    trackAuctionEvent({
        action: 'auction bid complete',
        label: bidStatus,
        itemId: item?.serviceId || '',
        value: String(bidAmount),
        eventName: EVENT_AUCTION_BID,
        step_interaction_name: STEP_INTERACTION_COMPLETE,
        trigger: getBidTrigger(hasBid),
        additional: {
            auction_type: auctionType,
            bid_status: bidStatus,
            bid_value: String(bidAmount),
            auction_time_remaining: String(minutesLeft),
        },
    });
}

export function trackAppDownloadCTA({ action }: { action: string }): void {
    trackEvent({
        action,
        label: 'bid confirmation',
        category: 'promo interaction',
    });
}

export function trackConfirmIncreaseMaxBid({
    bidAmount,
    isError,
    endDate,
}: {
    bidAmount: number;
    isError: boolean;
    endDate: Maybe<string>;
}): void {
    const minutesLeft = getAuctionTimeLeftInMinutes(endDate || '');

    trackAuctionEvent({
        action: 'auction bid interaction',
        label: 'auction PDP - confirm increase max bid click',
        eventName: EVENT_AUCTION_BID,
        step_interaction_name: STEP_INTERACTION_CONFIRM_MAX_BID_MODAL_CLICK,
        isInteractiveEvent: true,
        trigger: TRIGGER_INCREASE_MAX_BID_CLICK,
        additional: {
            bid_status: isError ? 'bid error' : 'successful',
            bid_value: String(bidAmount),
            auction_time_remaining: String(minutesLeft),
        },
    });
}
